import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = "/api";

// Convert VAPID base64url public key → Uint8Array for PushManager.subscribe
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * useWebPush — single hook that owns the PWA push lifecycle for the owner.
 *
 *  - exposes: { supported, swRegistered, permission, subscribed, vapidKey,
 *               busy, lastError, lastTest, register, enablePush, disablePush, sendTest, refresh }
 *  - registers /service-worker.js exactly once on mount
 *  - reads push subscription from the SW and tells the backend
 */
export default function useWebPush({ enabled = true } = {}) {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const [swRegistered, setSwRegistered] = useState(false);
  const [permission, setPermission] = useState(supported ? Notification.permission : "default");
  const [subscribed, setSubscribed] = useState(false);
  const [vapidKey, setVapidKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [lastTest, setLastTest] = useState(null);
  const [currentEndpointHash, setCurrentEndpointHash] = useState(null);

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      setSwRegistered(!!reg);
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
        if (sub) {
          try {
            const subJSON = sub.toJSON();
            const r = await axios.post(`${API}/push/subscribe`, {
              endpoint: subJSON.endpoint,
              keys: { p256dh: subJSON.keys?.p256dh, auth: subJSON.keys?.auth },
              user_agent: navigator.userAgent,
              platform: navigator.platform || "",
            });
            if (r?.data?.endpoint_hash) setCurrentEndpointHash(r.data.endpoint_hash);
          } catch (_) { /* best-effort sync; surfaced on next test */ }
        } else {
          setCurrentEndpointHash(null);
        }
      }
      setPermission(Notification.permission);
    } catch (e) {
      setLastError(e.message);
    }
  }, [supported]);

  // Register the SW + fetch VAPID public key
  useEffect(() => {
    if (!enabled || !supported) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register("/service-worker.js?v=1.2.0");
        // Force update check so iPhone picks up new SW logic without a reinstall
        try { reg.update(); } catch (_) { /* best-effort */ }
        setSwRegistered(true);
        // Wait until activated to ensure we can read the push subscription
        if (reg.installing) {
          await new Promise((res) => {
            reg.installing.addEventListener("statechange", () => {
              if (reg.active) res();
            });
          });
        }
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
        setPermission(Notification.permission);
        try {
          const r = await axios.get(`${API}/push/vapid-public-key`);
          setVapidKey(r.data.public_key || "");
        } catch (_) { /* will retry on enable */ }
      } catch (e) {
        setLastError(`sw: ${e.message}`);
      }
    })();
  }, [enabled, supported]);

  const enablePush = useCallback(async () => {
    if (!supported) {
      setLastError("Tu navegador no soporta notificaciones push.");
      return false;
    }
    setBusy(true); setLastError(null);
    try {
      // Corte 6 (G3) — desbloquear el AudioContext en el MISMO gesto del permiso
      try {
        const { unlockAudio } = await import("../utils/alertSound");
        unlockAudio();
      } catch (_) {}
      // 1. Permission
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setLastError("Permiso de notificaciones denegado.");
        return false;
      }

      // 2. SW registration
      const reg = await navigator.serviceWorker.ready;

      // 3. VAPID key
      let key = vapidKey;
      if (!key) {
        const r = await axios.get(`${API}/push/vapid-public-key`);
        key = r.data.public_key;
        setVapidKey(key);
      }
      if (!key) throw new Error("VAPID key not configured on backend");

      // 4. Subscribe (or reuse existing)
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        });
      }

      // 5. Send to backend
      const subJSON = sub.toJSON();
      const r = await axios.post(`${API}/push/subscribe`, {
        endpoint: subJSON.endpoint,
        keys: { p256dh: subJSON.keys.p256dh, auth: subJSON.keys.auth },
        user_agent: navigator.userAgent,
        platform: navigator.platform || "",
      });
      if (r?.data?.endpoint_hash) setCurrentEndpointHash(r.data.endpoint_hash);
      setSubscribed(true);
      return true;
    } catch (e) {
      setLastError(e?.response?.data?.detail || e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [supported, vapidKey]);

  const disablePush = useCallback(async () => {
    if (!supported) return;
    setBusy(true); setLastError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const subJSON = sub.toJSON();
        try { await axios.post(`${API}/push/unsubscribe`, { endpoint: subJSON.endpoint }); } catch (_) {}
        await sub.unsubscribe();
      }
      setSubscribed(false);
      setCurrentEndpointHash(null);
    } catch (e) {
      setLastError(e.message);
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    setBusy(true); setLastError(null); setLastTest(null);
    try {
      const r = await axios.post(`${API}/push/test`, {
        title: "AvisoCar",
        body: "Notificacion de prueba — si la ves, todo funciona.",
      });
      setLastTest(r.data);
      return r.data;
    } catch (e) {
      setLastError(e?.response?.data?.detail || e.message);
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    supported, swRegistered, permission, subscribed, vapidKey,
    busy, lastError, lastTest, currentEndpointHash,
    enablePush, disablePush, sendTest, refresh,
  };
}
